import { useState, useEffect } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Calculator, Loader2, Lock, CheckCircle2, BadgeCheck, Award } from "lucide-react";
import { toast } from "sonner";
import { IndicativeDisclaimer } from "@/components/IndicativeDisclaimer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INPUT_FIELDS = [
  { key: "plot_area_deed", label: "Plot Area — Conveyance/Index-II (sq.m)" },
  { key: "plot_area_survey", label: "Plot Area — Physical Survey (sq.m)" },
  { key: "existing_bua", label: "Existing BUA (sq.m)" },
  { key: "num_residential", label: "Residential Members" },
  { key: "num_commercial", label: "Commercial Members" },
  { key: "road_width_1", label: "Road Width (m)" },
  { key: "carpet_area_occupied", label: "Occupied Carpet (sq.ft)" },
  { key: "rr_land", label: "RR Rate — Land (per sq.m)" },
  { key: "rr_residential", label: "RR Rate — Residential (per sq.m)" },
  { key: "rr_office", label: "RR Rate — Office (per sq.m)" },
  { key: "rr_shop", label: "RR Rate — Shop (per sq.m)" },
];

export const FeasibilityStudio = () => {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [societies, setSocieties] = useState([]);
  const [societyId, setSocietyId] = useState("");
  const [schemeMeta, setSchemeMeta] = useState(null);
  const [regulation, setRegulation] = useState("");
  const [selectedSchemes, setSelectedSchemes] = useState([]);
  const [inputs, setInputs] = useState({});
  const [saleRates, setSaleRates] = useState("17000, 18000");
  const [computing, setComputing] = useState(false);
  const [options, setOptions] = useState([]);
  const [recommended, setRecommended] = useState(null);
  const [studyId, setStudyId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const [signoff, setSignoff] = useState({ professional_name: "", license_number: "" });
  const [finalizing, setFinalizing] = useState(false);

  useEffect(() => { init(); }, []);

  const init = async () => {
    try {
      const meRes = await fetch(`${API}/auth/me`, { credentials: "include" });
      const meData = meRes.ok ? await meRes.json() : null;
      setMe(meData);
      if (meData?.is_feasibility_analyst) {
        let list = [];
        const adminRes = await fetch(`${API}/admin/societies`, { credentials: "include" });
        if (adminRes.ok) list = await adminRes.json();
        else {
          const socRes = await fetch(`${API}/societies`, { credentials: "include" });
          if (socRes.ok) list = await socRes.json();
        }
        setSocieties(list);
      }
    } finally {
      setLoading(false);
    }
  };

  const onSelectSociety = async (sid) => {
    setSocietyId(sid);
    setOptions([]); setRecommended(null); setStudyId(null);
    const soc = societies.find(s => s.society_id === sid);
    if (soc) {
      setInputs({
        plot_area_deed: soc.plot_area || "",
        plot_area_survey: soc.plot_area || "",
        existing_bua: soc.builtup_area || "",
        num_residential: soc.total_flats || "",
        num_commercial: "",
        road_width_1: "",
        carpet_area_occupied: soc.carpet_area || "",
        rr_land: "", rr_residential: "", rr_office: "", rr_shop: "",
        zone: soc.zone || "",
      });
      const q = (soc.location_lat && soc.location_lng) ? `?lat=${soc.location_lat}&lng=${soc.location_lng}` : "";
      const res = await fetch(`${API}/feasibility/schemes${q}`, { credentials: "include" });
      if (res.ok) {
        const meta = await res.json();
        setSchemeMeta(meta);
        setRegulation(meta.regulation);
        setSelectedSchemes([]);
      }
    }
  };

  const onChangeRegulation = async (reg) => {
    setRegulation(reg);
    setSelectedSchemes([]);
    const soc = societies.find(s => s.society_id === societyId);
    const q = (soc?.location_lat && soc?.location_lng) ? `?lat=${soc.location_lat}&lng=${soc.location_lng}&regulation=${encodeURIComponent(reg)}` : `?regulation=${encodeURIComponent(reg)}`;
    const res = await fetch(`${API}/feasibility/schemes${q}`, { credentials: "include" });
    if (res.ok) setSchemeMeta(await res.json());
  };

  const toggleScheme = (code) => {
    setSelectedSchemes(prev => prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]);
  };

  const parseRates = () => saleRates.split(",").map(r => parseInt(r.trim())).filter(Boolean);

  const handleCompute = async () => {
    if (selectedSchemes.length < 1) { toast.error("Select at least one scheme to model"); return; }
    setComputing(true);
    try {
      const res = await fetch(`${API}/societies/${societyId}/feasibility/compute`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ regulation, scheme_codes: selectedSchemes, sale_rates: parseRates(), inputs }),
        credentials: "include"
      });
      const data = await res.json();
      if (res.ok) { setOptions(data.options); setRecommended(0); toast.success("Feasibility computed"); }
      else toast.error(data.detail || "Compute failed");
    } catch { toast.error("Compute failed"); }
    finally { setComputing(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const soc = societies.find(s => s.society_id === societyId);
      const res = await fetch(`${API}/societies/${societyId}/feasibility`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ study_id: studyId, title: `Feasibility — ${soc?.name || ""}`, regulation, inputs, options, recommended_index: recommended, sale_rates: parseRates() }),
        credentials: "include"
      });
      const data = await res.json();
      if (res.ok) { setStudyId(data.study_id); toast.success("Draft saved"); }
      else toast.error(data.detail || "Save failed");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const handleFinalize = async () => {
    if (!signoff.professional_name || !signoff.license_number) { toast.error("Professional name & license number required"); return; }
    setFinalizing(true);
    try {
      let sid = studyId;
      if (!sid) {
        const soc = societies.find(s => s.society_id === societyId);
        const saveRes = await fetch(`${API}/societies/${societyId}/feasibility`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ title: `Feasibility — ${soc?.name || ""}`, regulation, inputs, options, recommended_index: recommended, sale_rates: parseRates() }),
          credentials: "include"
        });
        const saved = await saveRes.json();
        sid = saved.study_id; setStudyId(sid);
      }
      const res = await fetch(`${API}/societies/${societyId}/feasibility/${sid}/finalize`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(signoff), credentials: "include"
      });
      const data = await res.json();
      if (res.ok) {
        toast.success("Report finalized & published to the society's Document Vault");
        setFinalizeOpen(false);
      } else toast.error(data.detail || "Finalize failed");
    } catch { toast.error("Finalize failed"); }
    finally { setFinalizing(false); }
  };

  if (loading) {
    return <DashboardLayout><div className="flex items-center justify-center h-96"><div className="spinner" /></div></DashboardLayout>;
  }

  if (!me?.is_feasibility_analyst) {
    return (
      <DashboardLayout>
        <div className="max-w-xl mx-auto py-16 text-center" data-testid="studio-locked">
          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-slate-500" />
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>Feasibility Studio</h2>
          <p className="text-slate-600">This internal tool is limited to staff with the <strong>Feasibility Analyst</strong> permission. Ask the Master Admin to grant access.</p>
        </div>
      </DashboardLayout>
    );
  }

  const maxProfit = Math.max(1, ...options.flatMap(o => o.scenarios.map(s => Math.abs(s.profit_cr))));

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="feasibility-studio">
        <div className="flex items-center gap-3">
          <Calculator className="w-7 h-7 text-emerald-700" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Feasibility Comparison Studio</h1>
            <p className="text-slate-600">Model redevelopment scheme options side by side, then sign off a Final report.</p>
          </div>
        </div>
        <IndicativeDisclaimer />

        {/* Society + jurisdiction */}
        <Card>
          <CardHeader><CardTitle className="text-lg">1. Society & Jurisdiction</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Society</Label>
                <Select value={societyId} onValueChange={onSelectSociety}>
                  <SelectTrigger data-testid="studio-society-select"><SelectValue placeholder="Select a society" /></SelectTrigger>
                  <SelectContent>
                    {societies.map(s => <SelectItem key={s.society_id} value={s.society_id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {schemeMeta && (
                <div>
                  <Label>Governing Regulation {schemeMeta.detected_regulation && <span className="text-xs text-slate-400">(detected: {schemeMeta.detected_regulation}{schemeMeta.detected_zone ? ` · ${schemeMeta.detected_zone}` : ""})</span>}</Label>
                  <Select value={regulation} onValueChange={onChangeRegulation}>
                    <SelectTrigger data-testid="studio-regulation-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {schemeMeta.all_regulations.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {schemeMeta.overridden && <p className="text-xs text-amber-600 mt-1">Manually overridden from auto-detected jurisdiction.</p>}
                </div>
              )}
            </div>

            {schemeMeta && (
              <div>
                <Label>Applicable Schemes (select 2+ to compare)</Label>
                <div className="grid md:grid-cols-2 gap-2 mt-2">
                  {schemeMeta.schemes.map(s => (
                    <label key={s.code} className={`flex items-start gap-2 p-3 rounded-lg border cursor-pointer ${selectedSchemes.includes(s.code) ? "border-emerald-500 bg-emerald-50" : "border-slate-200"}`} data-testid={`scheme-${s.code}`}>
                      <Checkbox checked={selectedSchemes.includes(s.code)} onCheckedChange={() => toggleScheme(s.code)} />
                      <span>
                        <span className="block text-sm font-medium text-slate-900">{s.label}</span>
                        <span className="block text-xs text-slate-500">{s.description}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Inputs */}
        {societyId && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">2. Property & Financial Inputs</CardTitle>
              <CardDescription>Pre-filled from the society profile where available — confirm or edit each value.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {INPUT_FIELDS.map(f => (
                  <div key={f.key}>
                    <Label className="text-xs">{f.label}</Label>
                    <Input
                      type="number" value={inputs[f.key] ?? ""}
                      onChange={(e) => setInputs({ ...inputs, [f.key]: e.target.value === "" ? "" : parseFloat(e.target.value) })}
                      data-testid={`input-${f.key}`}
                    />
                  </div>
                ))}
                <div>
                  <Label className="text-xs">Sale Rate Scenarios (₹/sq.ft, comma-separated)</Label>
                  <Input value={saleRates} onChange={(e) => setSaleRates(e.target.value)} data-testid="input-sale-rates" />
                </div>
              </div>
              <Button className="mt-4 bg-emerald-700 hover:bg-emerald-800" onClick={handleCompute} disabled={computing} data-testid="compute-btn">
                {computing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Computing…</> : <><Calculator className="w-4 h-4 mr-2" /> Compute Comparison</>}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Comparison */}
        {options.length > 0 && (
          <Card data-testid="comparison-studio">
            <CardHeader>
              <CardTitle className="text-lg">3. Comparison</CardTitle>
              <CardDescription>Select the recommended option, save a draft, then finalize with a professional sign-off.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="text-left text-slate-500 border-b">
                      <th className="py-2 pr-4">Metric</th>
                      {options.map((o, i) => (
                        <th key={i} className="py-2 px-3">
                          <label className="flex items-center gap-2 cursor-pointer" data-testid={`recommend-${i}`}>
                            <input type="radio" name="rec" checked={recommended === i} onChange={() => setRecommended(i)} />
                            <span className="font-semibold text-slate-800">{o.scheme_label.split("—")[0].trim()}</span>
                          </label>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    <tr><td className="py-2 pr-4 text-slate-500">Total Permissible BUA (sq.m)</td>{options.map((o,i)=><td key={i} className="py-2 px-3">{o.areas.total_bua_with_ancillary_sqm.toLocaleString()}</td>)}</tr>
                    <tr><td className="py-2 pr-4 text-slate-500">Balance Saleable Carpet (sq.ft)</td>{options.map((o,i)=><td key={i} className="py-2 px-3">{o.areas.balance_saleable_carpet_sqft.toLocaleString()}</td>)}</tr>
                    <tr><td className="py-2 pr-4 text-slate-500">Total Project Cost (₹ Cr)</td>{options.map((o,i)=><td key={i} className="py-2 px-3">{o.costs.R_total_project_cr}</td>)}</tr>
                    {options[0].scenarios.map((_, si) => (
                      <tr key={si}>
                        <td className="py-2 pr-4 text-slate-500">Profit @ ₹{options[0].scenarios[si].sale_rate}/sqft</td>
                        {options.map((o,i)=>(
                          <td key={i} className="py-2 px-3">
                            <span className={o.scenarios[si].profit_cr >= 0 ? "text-emerald-700 font-medium" : "text-red-600 font-medium"}>
                              ₹{o.scenarios[si].profit_cr} Cr ({o.scenarios[si].profit_pct}%)
                            </span>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Profit bar chart (first scenario) */}
              <div>
                <p className="text-xs text-slate-500 mb-2">Profit @ ₹{options[0].scenarios[0].sale_rate}/sqft</p>
                <div className="space-y-2">
                  {options.map((o, i) => {
                    const p = o.scenarios[0].profit_cr;
                    const w = Math.min(100, (Math.abs(p) / maxProfit) * 100);
                    return (
                      <div key={i} className="flex items-center gap-2">
                        <span className="w-24 text-xs text-slate-600 truncate">{o.scheme_code}</span>
                        <div className="flex-1 h-4 bg-slate-100 rounded">
                          <div className={`h-4 rounded ${p >= 0 ? "bg-emerald-500" : "bg-red-400"}`} style={{ width: `${w}%` }} />
                        </div>
                        <span className="w-24 text-right text-xs font-medium">₹{p} Cr</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                <Button variant="outline" onClick={handleSave} disabled={saving} data-testid="save-draft-btn">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Save Draft
                </Button>
                <Button className="bg-emerald-700 hover:bg-emerald-800" onClick={() => setFinalizeOpen(true)} disabled={recommended === null} data-testid="open-finalize-btn">
                  <Award className="w-4 h-4 mr-2" /> Mark Final & Publish
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Finalize dialog */}
        <Dialog open={finalizeOpen} onOpenChange={setFinalizeOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle className="flex items-center gap-2"><BadgeCheck className="w-5 h-5 text-emerald-600" /> Professional Sign-off</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-slate-600">A report can only be marked Final once a licensed professional stands behind the figures. This is recorded on the published report.</p>
              <div>
                <Label>Licensed Professional Name *</Label>
                <Input value={signoff.professional_name} onChange={(e) => setSignoff({ ...signoff, professional_name: e.target.value })} placeholder="Ar. / Er. Full Name" data-testid="signoff-name-input" />
              </div>
              <div>
                <Label>License / Registration Number *</Label>
                <Input value={signoff.license_number} onChange={(e) => setSignoff({ ...signoff, license_number: e.target.value })} placeholder="e.g. CA/2018/97979" data-testid="signoff-license-input" />
              </div>
              <Button className="w-full bg-emerald-700 hover:bg-emerald-800" onClick={handleFinalize} disabled={finalizing} data-testid="confirm-finalize-btn">
                {finalizing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Finalizing…</> : <><CheckCircle2 className="w-4 h-4 mr-2" /> Finalize & Publish to Vault</>}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default FeasibilityStudio;
